import AbstractAuth from './abstract-auth';
import history from '@optimusride/client-library/utils/history';
import 'firebase/auth';
import { firebase } from '../utils/firebase';
import {
  EMAIL_FOR_SIGN_IN_LS_KEY,
  OPERATING_AREA_ID_LS_KEY,
  PHONE_FOR_SIGN_IN_LS_KEY,
} from '@optimusride/client-library/constants';

export default class Auth extends AbstractAuth {
  public login = (email: string, password: string): Promise<firebase.auth.UserCredential> => {
    return new Promise((resolve, reject) => {
      firebase.auth().signInWithEmailAndPassword(email, password)
        .then(async user => {
          if (await this.isAuthorized()) {
            resolve(user);
          } else {
            this.logout();
            reject(this.emptyError());
          }
        })
        .catch((err: Error) => {
          console.log(err);
          this.logout();
          reject(err);
        });
    });
  }

  public loginWithGSuite = (): Promise<firebase.auth.UserCredential> => {
    return new Promise((resolve, reject) => {
      const provider = new firebase.auth.GoogleAuthProvider();
      provider.setCustomParameters({
        hd: 'optimusride.com',
        prompt: 'select_account',
      });
      firebase.auth().signInWithPopup(provider)
        .then(async user => {
          if (await this.isAuthorized()) {
            resolve(user);
          } else {
            this.logout();
            reject(this.emptyError());
          }
        })
        .catch(error => {
          console.log(error);
          this.logout();
          reject(error);
        });
    });
  }

  public sendPhoneCode = (_phoneNumber: string): Promise<firebase.auth.ConfirmationResult> => {
    throw new Error('Not implemented');
  }

  public loginWithPhone = (_confirmationResult: firebase.auth.ConfirmationResult, _code: string): Promise<firebase.auth.UserCredential> => {
    throw new Error('Not implemented');
  }

  public sendEmailLink = (email: string) => {
    const url = `${window.location.protocol}//${window.location.host}`;
    const actionCodeSettings = {
      // TODO: This link is the redirect link. Edit to redirect to the new screen.
      url: `${url}/auth`,
      // This must be true.
      handleCodeInApp: true,
    };
    return firebase.auth().sendSignInLinkToEmail(email, actionCodeSettings);
  }

  public loginWithEmailLink = (link: string, email: string): Promise<firebase.auth.UserCredential> => {
    return new Promise(resolve => {
      if (firebase.auth().isSignInWithEmailLink(link)) {
        firebase.auth().signInWithEmailLink(email, link)
          .then(async user => {
            resolve(user);
          });
      }
    });
  }

  public onAuthStateChanged = (handler: (user: firebase.User | null) => void) => {
    return firebase.auth().onAuthStateChanged(handler);
  }

  public logout = (path: string = '/login') => {
    return firebase.auth().signOut().then(() => {
      window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_LS_KEY);
      window.localStorage.removeItem(PHONE_FOR_SIGN_IN_LS_KEY);
      window.localStorage.removeItem(OPERATING_AREA_ID_LS_KEY);
      history.replace(path);
    });
  }

  public currentUser = () => {
    return firebase.auth().currentUser;
  }

  public resetUserPassword = (email: string = ''): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      firebase.auth().sendPasswordResetEmail(email).then(() => {
        resolve(true);
      }).catch(err => {
        console.log(err);
        reject(err);
      })
    });
  }

  public updateUserPassword = (newPassword: string): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      const user = firebase.auth().currentUser;
      if (user) {
        user.updatePassword(newPassword).then(() => {
          resolve(true);
        }).catch(err => {
          console.log(err);
          reject(err);
        });
      }
    });
  }
}
